import React, { useState, useEffect } from "react";
import {
  Alert,
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import RequiredLabel from "../../shared/RequiredLabel";
import { formatJpDateTime } from "../../utils/dateUtils";

// 初期フォーム値
const EMPTY_FORM = {
  desired_amount: "",
  start_date: "",
  technical_skill: "",
  remark: "",
  work_style: "on_site",
};

const ProjectRequestEdit = ({ csrfToken }) => {
  const navigate = useNavigate();

  const [userContractRequest, setUserContractRequest] = useState(null);
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState([]);
  const [validationTriggered, setValidationTriggered] = useState(false);

  // 初期データ取得
  useEffect(() => {
    const fetchData = async () => {
      const res = await fetch("/user_contract_request/edit.json");
      const data = await res.json();

      const req = data.contract_request;

      setUserContractRequest(req);

      setFormState({
        desired_amount: req?.desired_amount || "",
        start_date: req?.start_date || "",
        technical_skill: req?.technical_skill || "",
        remark: req?.remark || "",
        work_style: req?.work_style || "on_site",
      });
    };

    fetchData();
  }, []);

  const params = new URLSearchParams(window.location.search);
  const redirectPath = params.get("redirect") || "/projects";

  // エラーが出たらページ上部にスクロール
  useEffect(() => {
    if (validationTriggered && errors.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationTriggered]);

  // 指定された日付が過去かを判定（空文字は除外）
  const isDateInPast = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  // 数値が空または負の値か
  const isEmptyOrNegative = (value) => {
    const num = Number(value);
    return value === "" || isNaN(num) || num < 0;
  };

  // 空文字・null・undefined を空とみなす
  const isEmpty = (value) =>
    value === "" || value === null || value === undefined;

  // フィールド別のエラーフラグ（赤枠表示用）
  const isInvalidTechnicalSkill =
    validationTriggered && isEmpty(formState.technical_skill);
  const isInvalidStartDate =
    validationTriggered &&
    (isEmpty(formState.start_date) || isDateInPast(formState.start_date));
  const isInvalidDesiredAmount =
    validationTriggered &&
    (isEmpty(formState.desired_amount) ||
      isEmptyOrNegative(formState.desired_amount));

  const validateForm = () => {
    const newErrors = [];

    const rules = [
      {
        field: "desired_amount",
        isError: isEmpty(formState.desired_amount),
        message: "希望金額は入力必須です。",
      },
      {
        field: "desired_amount",
        isError:
          !isEmpty(formState.desired_amount) &&
          isEmptyOrNegative(formState.desired_amount),
        message: "希望金額は0以上を入力してください。",
      },
      {
        field: "start_date",
        isError: isEmpty(formState.start_date),
        message: "希望開始時期は入力必須です。",
      },
      {
        field: "start_date",
        isError:
          !isEmpty(formState.start_date) && isDateInPast(formState.start_date),
        message: "希望開始時期は今日以降の日付を指定してください。",
      },
      {
        field: "technical_skill",
        isError: isEmpty(formState.technical_skill),
        message: "得意スキルは入力必須です。",
      },
    ];

    rules.forEach((rule) => {
      if (rule.isError) {
        newErrors.push(rule.message);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // フォーム送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationTriggered(true);

    if (!validateForm()) return;

    try {
      const res = await fetch("/user_contract_request", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          user_contract_request: formState,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === "ok") {
        toast.success("更新しました。");
        navigate(`${redirectPath}`);
      } else {
        toast.error("更新に失敗しました。");
        if (data.errors) setErrors(data.errors);
      }
    } catch (error) {
      toast.error("通信エラーが発生しました。");
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setValidationTriggered(false);
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleWorkStyleChange = (_, newValue) => {
    if (newValue !== null) {
      setValidationTriggered(false);
      setFormState({ ...formState, work_style: newValue });
    }
  };

  const characterCount = formState.remark ? formState.remark.length : 0;

  // Loading
  if (!userContractRequest) {
    return (
      <Box p={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ maxWidth: 960, mx: "auto", position: "relative", mt: 2, mb: 10 }}
    >
      <Button
        onClick={() => window.history.back()}
        size="small"
        sx={{
          mb: 0.5,
          color: "#000",
          fontWeight: "bold",
          textTransform: "none",
          "&:hover": { opacity: 0.7 },
        }}
      >
        ← 戻る
      </Button>

      <form id="user-contract-request-form" onSubmit={handleSubmit}>
        <Box
          sx={{
            bgcolor: "#fff",
            p: { xs: 2, sm: 4 },
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          {validationTriggered && errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.map((error, idx) => (
                <div key={idx}>{error}</div>
              ))}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              プロジェクトの希望条件
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "block",
                borderBottom: "1px solid #ccc",
                pb: 1,
              }}
            >
              更新日時：{formatJpDateTime(userContractRequest?.updated_at)}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>希望金額（税別）</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="desired_amount"
                type="number"
                value={formState.desired_amount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 6) handleChange(e);
                }}
                placeholder="70"
                error={isInvalidDesiredAmount}
                sx={{
                  "& .MuiOutlinedInput-root input": {
                    boxShadow: "none",
                    outline: "none",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">万円</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>希望開始時期</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="start_date"
                type="date"
                value={formState.start_date}
                onChange={handleChange}
                error={isInvalidStartDate}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>稼働形態</Typography>
                <RequiredLabel show={true} />
              </Box>
              <ToggleButtonGroup
                exclusive
                value={formState.work_style}
                onChange={handleWorkStyleChange}
                fullWidth
                size="small"
              >
                <ToggleButton value="on_site">常駐</ToggleButton>
                <ToggleButton value="remote">フルリモート</ToggleButton>
                <ToggleButton value="hybrid">ハイブリッド</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>得意スキル</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="technical_skill"
                value={formState.technical_skill}
                onChange={handleChange}
                placeholder="React、Rails、詳細設計"
                error={isInvalidTechnicalSkill}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                備考(最大200文字まで)
              </Typography>
              <TextField
                fullWidth
                name="remark"
                value={formState.remark}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="補足事項や特記事項をご記入ください"
                sx={{ "& textarea:focus": { boxShadow: "none" } }}
                helperText={`${characterCount}文字`}
                inputProps={{ maxLength: 200 }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    marginRight: "0",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              right: 16,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(redirectPath)}
              sx={{
                backgroundColor: "#ffffff",
                color: "#000",
                borderRadius: 9999,
                px: 4,
                fontWeight: "bold",
                border: "1px solid #9ca3af",
                "&:hover": {
                  opacity: 0.9,
                  backgroundColor: "#f3f4f6",
                  borderColor: "#9ca3af",
                },
                textAlign: "center",
              }}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="inherit"
              sx={{
                borderRadius: 4,
                color: "#fff",
                bgcolor: "#000",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              更新
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default ProjectRequestEdit;
